import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

// Layout mode is a per-device axis, independent of theme (6.1: the till is a
// fixed all-day device). "pos" grows touch targets and type for a counter
// touchscreen; "standard" is the desktop/mouse density. Like the theme, it is
// applied via a `data-*` attribute on <html> so the control-sizing tokens in
// index.css ([data-layout="pos"]) re-point at runtime, no rebuild.
export type LayoutMode = "standard" | "pos";
const STORAGE_KEY = "klutch-layout";

interface LayoutContextValue {
  mode: LayoutMode;
  pos: boolean;
  toggle: () => void;
  setMode: (m: LayoutMode) => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

function readInitial(): LayoutMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "pos" || saved === "standard") return saved;
  } catch {
    /* ignore */
  }
  return "standard";
}

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<LayoutMode>(readInitial);

  useEffect(() => {
    // Only set the attribute in POS mode; standard mode falls back to the
    // default tokens (no attribute), keeping the DOM clean.
    if (mode === "pos") document.documentElement.dataset.layout = "pos";
    else delete document.documentElement.dataset.layout;
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  const setMode = useCallback((m: LayoutMode) => setModeState(m), []);
  const toggle = useCallback(() => setModeState((m) => (m === "pos" ? "standard" : "pos")), []);

  const value = useMemo(() => ({ mode, pos: mode === "pos", toggle, setMode }), [mode, toggle, setMode]);
  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayoutMode(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error("useLayoutMode must be used within <LayoutProvider>");
  return ctx;
}
